// src/pages/gro/DimensionRiskRuleFormPage.tsx
import { useEffect, useState } from "react";
import { ChevronLeft, Save } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { PERMISSIONS } from "@/lib/permissions";
import { usePermission } from "@/hooks/usePermission";
import {
  useCreateQuestionnaireDimensionRiskRule,
  useQuestionnaireDimensionRiskRuleDetail,
  useQuestionnaireDimensionRiskRuleLookups,
  useUpdateQuestionnaireDimensionRiskRule,
} from "@/hooks/useQuestionnaireDimensionRiskRules";
import type {
  DimensionRiskRuleOperator,
  QuestionnaireDimensionRiskRuleUpsertIn,
} from "@/types/questionnaireDimensionRiskRules";

const DEFAULT_FORM: QuestionnaireDimensionRiskRuleUpsertIn = {
  dimension_id: "",
  risk_catalog_id: "",
  operator: "gte",
  threshold_value: 3,
  suggested_probability_level_id: null,
  suggested_severity_level_id: null,
  priority: 1,
  auto_generate_inventory_draft: false,
  is_active: true,
};

const OPERATOR_OPTIONS: { value: DimensionRiskRuleOperator; label: string }[] = [
  { value: "gt", label: "Quando a média da dimensão for maior que" },
  { value: "gte", label: "Quando a média da dimensão for maior ou igual a" },
  { value: "lt", label: "Quando a média da dimensão for menor que" },
  { value: "lte", label: "Quando a média da dimensão for menor ou igual a" },
  { value: "eq", label: "Quando a média da dimensão for igual a" },
];

const PRIORITY_OPTIONS = [
  { value: 1, label: "Alta" },
  { value: 2, label: "Média" },
  { value: 3, label: "Baixa" },
] as const;

export default function DimensionRiskRuleFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const isCreating = !id;
  const isEditingRoute = location.pathname.endsWith("/editar");
  const canWrite = usePermission(PERMISSIONS.QUESTIONNAIRES_WRITE);
  const isReadOnly = !isCreating && !isEditingRoute;

  const detailQ = useQuestionnaireDimensionRiskRuleDetail(id);
  const lookupsQ = useQuestionnaireDimensionRiskRuleLookups();

  const createMut = useCreateQuestionnaireDimensionRiskRule();
  const updateMut = useUpdateQuestionnaireDimensionRiskRule();

  const [form, setForm] = useState<QuestionnaireDimensionRiskRuleUpsertIn>(
    DEFAULT_FORM,
  );

  useEffect(() => {
    if (!detailQ.data) return;

    setForm({
      dimension_id: detailQ.data.dimension_id,
      risk_catalog_id: detailQ.data.risk_catalog_id,
      operator: detailQ.data.operator,
      threshold_value: detailQ.data.threshold_value,
      suggested_probability_level_id:
        detailQ.data.suggested_probability_level_id || null,
      suggested_severity_level_id:
        detailQ.data.suggested_severity_level_id || null,
      priority: detailQ.data.priority,
      auto_generate_inventory_draft: detailQ.data.auto_generate_inventory_draft,
      is_active: detailQ.data.is_active,
    });
  }, [detailQ.data]);

  const dimensions = lookupsQ.data?.dimensions ?? [];
  const risks = lookupsQ.data?.risks ?? [];
  const probabilityLevels = lookupsQ.data?.probability_levels ?? [];
  const severityLevels = lookupsQ.data?.severity_levels ?? [];

  const saving = createMut.isPending || updateMut.isPending;

  function handleField<K extends keyof QuestionnaireDimensionRiskRuleUpsertIn>(
    field: K,
    value: QuestionnaireDimensionRiskRuleUpsertIn[K],
  ) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit() {
    if (isReadOnly || !canWrite) return;
    if (!form.dimension_id) {
      toast.error("Selecione a dimensão.");
      return;
    }
    if (!form.risk_catalog_id) {
      toast.error("Selecione o risco.");
      return;
    }
    if (!form.suggested_severity_level_id) {
      toast.error("Selecione a severidade sugerida.");
      return;
    }
    if (form.priority < 1) {
      toast.error("A prioridade deve ser maior ou igual a 1.");
      return;
    }

    try {
      if (id) {
        await updateMut.mutateAsync({ ruleId: id, body: form });
        toast.success("Regra atualizada com sucesso.");
        navigate(`/gro/dimension-risk-rules/${id}`);
        return;
      }

      const created = await createMut.mutateAsync(form);
      toast.success("Regra criada com sucesso.");
      navigate(`/gro/dimension-risk-rules/${created.id}`);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar regra.");
    }
  }

  return (
    <div>
      <PageHeader
        title={
          isCreating
            ? "Nova Regra Dimensão x Risco"
            : isReadOnly
              ? "Visualizar Regra Dimensão x Risco"
              : "Editar Regra Dimensão x Risco"
        }
        description="Defina qual risco será sugerido quando a média de uma dimensão atingir a condição configurada."
      >
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/gro/dimension-risk-rules`)}>
            <ChevronLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>

          {!isReadOnly ? (
            <Button onClick={handleSubmit} disabled={saving}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          ) : null}
        </div>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>Configuração da regra</CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Dimensão *</Label>
              <Select
                value={form.dimension_id || undefined}
                onValueChange={(value) => handleField("dimension_id", value)}
                disabled={isReadOnly}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {dimensions.map((dimension) => (
                    <SelectItem key={dimension.id} value={dimension.id}>
                      {dimension.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Risco sugerido *</Label>
              <Select
                value={form.risk_catalog_id || undefined}
                onValueChange={(value) => handleField("risk_catalog_id", value)}
                disabled={isReadOnly}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {risks.map((risk) => (
                    <SelectItem key={risk.id} value={risk.id}>
                      {risk.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <Label>Condição *</Label>
              <Select
                value={form.operator}
                onValueChange={(value) =>
                  handleField(
                    "operator",
                    value as QuestionnaireDimensionRiskRuleUpsertIn["operator"],
                  )
                }
                disabled={isReadOnly}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OPERATOR_OPTIONS.map((operator) => (
                    <SelectItem key={operator.value} value={operator.value}>
                      {operator.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Valor da média *</Label>
              <Input
                className="mt-1"
                type="number"
                step="0.01"
                value={form.threshold_value}
                onChange={(e) =>
                  handleField("threshold_value", Number(e.target.value || 0))
                }
                disabled={isReadOnly}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Probabilidade sugerida</Label>
              <Select
                value={form.suggested_probability_level_id ?? "auto"}
                onValueChange={(value) =>
                  handleField(
                    "suggested_probability_level_id",
                    value === "auto" ? null : value,
                  )
                }
                disabled={isReadOnly}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Definir automaticamente</SelectItem>
                  {probabilityLevels.map((level) => (
                    <SelectItem key={level.id} value={level.id}>
                      {level.name} ({Number(level.score).toFixed(2)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Severidade sugerida *</Label>
              <Select
                value={form.suggested_severity_level_id ?? undefined}
                onValueChange={(value) =>
                  handleField("suggested_severity_level_id", value || null)
                }
                disabled={isReadOnly}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {severityLevels.map((level) => (
                    <SelectItem key={level.id} value={level.id}>
                      {level.name} ({Number(level.score).toFixed(2)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>Prioridade *</Label>
              <Select
                value={String(form.priority)}
                onValueChange={(value) => handleField("priority", Number(value))}
                disabled={isReadOnly}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Gera rascunho no inventário</Label>
              <Select
                value={form.auto_generate_inventory_draft ? "yes" : "no"}
                onValueChange={(value) =>
                  handleField("auto_generate_inventory_draft", value === "yes")
                }
                disabled={isReadOnly}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">Não</SelectItem>
                  <SelectItem value="yes">Sim</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-muted-foreground">
                Com “Sim”, ao aprovar a sugestão o sistema já envia automaticamente o risco para o inventário como rascunho.
              </p>
            </div>

            <div>
              <Label>Status</Label>
              <Select
                value={form.is_active ? "active" : "inactive"}
                onValueChange={(value) => handleField("is_active", value === "active")}
                disabled={isReadOnly}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Ativa</SelectItem>
                  <SelectItem value="inactive">Inativa</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
